/**
 * Auth Provider (BFF-backed)
 *
 * Provides authentication context via the BFF token broker.
 * No MSAL browser libraries needed — the BFF handles all OAuth flows server-side.
 * The browser only holds an httpOnly session cookie.
 */

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import { setImpersonation, bffFetch } from '../api';
import { userActivityTracker } from '../entities/user-activity';

// =============================================================================
// Types
// =============================================================================

interface AuthUser {
  oid: string;
  name: string;
  email: string;
  roles: string[];
}

interface ImpersonationState {
  /** The user being impersonated */
  targetOid: string;
  targetName: string;
  targetEmail: string;
  /** The real admin user */
  realUser: { oid: string; name: string; email: string };
}

interface AuthContextValue {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth state is being determined */
  isLoading: boolean;
  /** The current user profile (impersonated user when impersonating) */
  user: AuthUser | null;
  /** User's display name */
  displayName: string | null;
  /** User's email */
  email: string | null;
  /** User's Entra App Roles */
  roles: string[];
  /** Redirect to BFF login */
  login: () => void;
  /** Destroy session and redirect to Azure AD logout */
  logout: () => Promise<void>;
  /** Get a Dataverse access token (cached or refreshed via BFF) */
  getAccessToken: () => Promise<string>;
  /** Current impersonation state, or null */
  impersonation: ImpersonationState | null;
  /** Start impersonating another user (admin only) */
  startImpersonation: (target: { oid: string; name: string; email: string }) => Promise<void>;
  /** Stop impersonating and return to admin identity */
  stopImpersonation: () => Promise<void>;
}

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to check if the current user has an admin role for a specific app.
 * @param appPrefix — e.g., "Sales", "Catalog", "Ops". If omitted, matches
 * the global `Admin` role or any `<Prefix>.Admin` role.
 */
export function useIsAdmin(appPrefix?: string): boolean {
  const { roles } = useAuth();
  if (appPrefix) {
    return roles.includes('Admin') || roles.includes(`${appPrefix}.Admin`);
  }
  return roles.some((r) => r === 'Admin' || r.endsWith('.Admin'));
}

/**
 * Hook to check if the current user can act on behalf of others in a module
 * — manager-or-admin tier. Admin always implies manager.
 *
 * Use for "edit on behalf of any rep" affordances. Configuration screens
 * should still use `useIsAdmin`.
 *
 * @param appPrefix — e.g., "Sales", "Catalog", "Ops". If omitted, matches
 * the global `Admin` role or any `<Prefix>.Admin`/`<Prefix>.Manager` role.
 */
export function useIsManager(appPrefix?: string): boolean {
  const { roles } = useAuth();
  if (appPrefix) {
    return (
      roles.includes('Admin') ||
      roles.includes(`${appPrefix}.Admin`) ||
      roles.includes(`${appPrefix}.Manager`)
    );
  }
  return roles.some((r) => r === 'Admin' || r.endsWith('.Admin') || r.endsWith('.Manager'));
}

// =============================================================================
// Provider
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [impersonation, setImpersonationState] = useState<ImpersonationState | null>(null);

  // Token cache: store the access token and its expiry in refs to avoid re-renders
  const cachedToken = useRef<string | null>(null);
  const tokenExpiresAt = useRef<Date | null>(null);

  // Mutex: if a token refresh is in flight, all callers wait for the same promise
  const refreshPromise = useRef<Promise<string> | null>(null);

  /**
   * Check if user has an active BFF session (called on mount / page refresh)
   */
  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch('/auth/me', { credentials: 'include' });
      if (response.ok) {
        const userData = await response.json();
        setUser({ oid: userData.oid, name: userData.name, email: userData.email, roles: userData.roles });
        userActivityTracker.setIdentity({
          oid: userData.oid,
          name: userData.name,
          email: userData.email,
        });

        // Restore impersonation state from server session
        if (userData.impersonating && userData.realUser) {
          const impState: ImpersonationState = {
            targetOid: userData.oid,
            targetName: userData.name,
            targetEmail: userData.email,
            realUser: userData.realUser,
          };
          setImpersonationState(impState);
          setImpersonation(userData.oid); // sync Dataverse client
        }
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /**
   * Redirect to BFF login endpoint (initiates full OAuth flow)
   */
  const login = useCallback(() => {
    window.location.href = '/auth/login';
  }, []);

  /**
   * Log out: destroy server session, redirect to Azure AD logout
   */
  const logout = useCallback(async () => {
    try {
      await userActivityTracker.trackLogout();
      const response = await bffFetch('/auth/logout', { method: 'POST' });
      if (response.ok) {
        const { logoutUrl } = await response.json();
        setUser(null);
        cachedToken.current = null;
        tokenExpiresAt.current = null;
        window.location.href = logoutUrl;
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, []);

  /**
   * Get a valid Dataverse access token.
   * Returns cached token if still fresh (>5min remaining),
   * otherwise fetches a new one from the BFF.
   * Concurrent callers share a single in-flight request (mutex).
   */
  const getAccessToken = useCallback(async (): Promise<string> => {
    const bufferMs = 5 * 60 * 1000;
    if (
      cachedToken.current &&
      tokenExpiresAt.current &&
      tokenExpiresAt.current.getTime() - Date.now() > bufferMs
    ) {
      return cachedToken.current;
    }

    // If a refresh is already in flight, wait for it
    if (refreshPromise.current) {
      return refreshPromise.current;
    }

    // Start a new refresh
    refreshPromise.current = (async () => {
      try {
        const response = await fetch('/auth/token', { credentials: 'include' });

        if (response.status === 401) {
          setUser(null);
          cachedToken.current = null;
          tokenExpiresAt.current = null;
          window.location.href = '/auth/login';
          throw new Error('Authentication required');
        }

        if (!response.ok) {
          throw new Error(`Token fetch failed: ${response.statusText}`);
        }

        const data = await response.json();
        cachedToken.current = data.accessToken;
        tokenExpiresAt.current = data.expiresOn ? new Date(data.expiresOn) : null;
        return data.accessToken as string;
      } finally {
        refreshPromise.current = null;
      }
    })();

    return refreshPromise.current;
  }, []);

  /**
   * Start impersonating another user. Calls BFF to persist in session,
   * then updates local state and Dataverse client.
   */
  const startImpersonation = useCallback(async (target: { oid: string; name: string; email: string }) => {
    const response = await bffFetch('/auth/impersonate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetOid: target.oid, targetName: target.name, targetEmail: target.email }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Impersonation failed' }));
      throw new Error(err.message || 'Impersonation failed');
    }

    const realUser = user ? { oid: user.oid, name: user.name, email: user.email } : { oid: '', name: '', email: '' };
    const impState: ImpersonationState = {
      targetOid: target.oid,
      targetName: target.name,
      targetEmail: target.email,
      realUser,
    };

    setImpersonationState(impState);
    setImpersonation(target.oid); // sync Dataverse client header

    // Clear token cache so next request picks up impersonation metadata
    cachedToken.current = null;
    tokenExpiresAt.current = null;

    // Pull the impersonated identity + role-stripped roles from /auth/me.
    // The BFF downgrades any .Admin roles to .Users when impersonation is
    // active, so the UI gates correctly. Setting `user` from the local
    // `target` would carry over the admin's roles, leaving Admin-only UI
    // visible (security bug — fixed 2026-04-28).
    await checkAuth();
  }, [user, checkAuth]);

  /**
   * Stop impersonating and return to the admin's own identity.
   */
  const stopImpersonation = useCallback(async () => {
    await bffFetch('/auth/stop-impersonate', { method: 'POST' });

    setImpersonation(null); // clear Dataverse client header
    setImpersonationState(null);

    // Clear token cache
    cachedToken.current = null;
    tokenExpiresAt.current = null;

    // Re-fetch the real user profile
    await checkAuth();
  }, [checkAuth]);

  // Note: Dataverse client initialization is handled by DataverseOrgProvider,
  // which owns the base URL (dev/prod) and token provider. AuthProvider only
  // provides the getAccessToken function for components that need it directly.

  const contextValue: AuthContextValue = useMemo(
    () => ({
      isAuthenticated: !!user,
      isLoading,
      user,
      displayName: user?.name ?? null,
      email: user?.email ?? null,
      roles: user?.roles ?? [],
      login,
      logout,
      getAccessToken,
      impersonation,
      startImpersonation,
      stopImpersonation,
    }),
    [user, isLoading, login, logout, getAccessToken, impersonation, startImpersonation, stopImpersonation]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
